import React, { useEffect, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { array, object, string } from "yup";
import { FollowUpFormValues } from "src/models/FollowUp.model";
import FollowUpForm from "../Layouts/FollowUpForm";
import {
  useEditFollowUpMutation,
  useGetFollowUpByIdQuery,
} from "src/services/FollowUpServices";
import { showToast } from "src/utils/showToaster";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import moment from "moment";

const EditFollowUpFormWrapper = () => {
  const { inquiryId = "" } = useParams();
  const { followupId = "" } = useParams();
  const navigate = useNavigate();
  const [editFollowUp] = useEditFollowUpMutation();

  const { data, isLoading, isFetching } = useGetFollowUpByIdQuery({
    id: followupId,
  });
  const [followUpData, setFollowUpData] = useState<any>();

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setFollowUpData(data?.data);
    }
  }, [data, isLoading, isFetching]);

  // Form Initial Values
  const initialValues: FollowUpFormValues = {
    is_communicated: followUpData?.is_communicated,
    inquiryLabel:{
      label:followUpData?.inquiryLabelName ,
      value : followUpData?.inquiryLabelId
    },
    communication_summary: followUpData?.communication_summary,
    communication_method: followUpData?.communication_method,
    interest_level: followUpData?.interest_level,
    next_followUp_required: followUpData?.next_followUp_required,
    contact_them: followUpData?.contact_them,
    next_followUp_date: followUpData?.next_followUp_date,
    next_followup_time: followUpData?.next_followup_time
      ? moment(
          followUpData?.next_followup_time?.split("-")?.[0]?.trim(),
          "hh:mm A"
        )
      : null,
    new_batch_start: followUpData?.new_batch_start,
    followup_batch_start_course: followUpData?.followup_batch_start_course?.map(
      (el: any) => {
        return {
          label: el?.courseName,
          value: el?._id,
        };
      }
    ),
    next_followup_reason: followUpData?.next_followup_reason,
    course_not_clear: followUpData?.course_not_clear,
    interested_course: followUpData?.interested_course?.map((el: any) => {
      return {
        course_name: {
          label: el?.courseLabel?.courseName,
          value: el?.courseLabel?._id,
        },
        fees: el?.fees,
        Offer_Price: el?.Offer_Price,
        remark: el?.remark,
      };
    }),
    disposition_one: followUpData?.disposition_one,
    disposition_two: followUpData?.disposition_two,
  };

  // Validation Schema
  const validationSchema = object().shape({
    is_communicated:string().required('please select communicated') ,
    communication_method:string().required('Please select communication method'),
    communication_summary:string().test(
      "address-test",
      "summary  is required",
      (value, context) => { 
        const { is_communicated } = context.parent;
        // const pervious = perviousMembershipPrice;
        if (is_communicated === "YES" && !value) {
          return false;
        }
        return true;
      }
    ),
    next_followUp_required: string().required('Please select next followup is required'),
    next_followUp_date: string()
      .nullable()
      .test(
        "address-test",
        "next followup date is required",
        (value, context) => {
          const { next_followUp_required } = context.parent;
          // const pervious = perviousMembershipPrice;
          if (next_followUp_required === "YES" && !value) {
            return false;
          }
          return true;
        }
      ),
    next_followup_time: string()
      .nullable()
      .test(
        "address-test",
        "next followup time is required",
        (value, context) => {
          const { next_followUp_required } = context.parent;
          // const pervious = perviousMembershipPrice;
          if (next_followUp_required?.value === "YES" && !value) {
            return false;
          }
          return true;
        }
      ),



    interested_course: array().of(
      object({
        course_name: object().required('please select course name'),
        fees: string().required('please enter total fees'),
        Offer_Price: string().required('please enter offer fees'),
      }),
    ),
    disposition_one: string().required('please select disposition one') ,
    disposition_two: string().required('please select disposition two') ,
    inquiryLabel : object().required('please select inquiry label') ,
  });

  // Handle Submit
  const handleSubmit = (
    values: FollowUpFormValues,
    { setSubmitting, resetForm }: FormikHelpers<FollowUpFormValues>
  ) => {
    const formattedValues = {
      inquiry_id: inquiryId,
      is_communicated: values?.is_communicated,
      communication_summary: values?.communication_summary,
      communication_method: values?.communication_method,
      interest_level: values?.interest_level,
      next_followUp_required: values?.next_followUp_required,
      contact_them: values?.contact_them,
      inquiryLabelId : values?.inquiryLabel?.value,
      next_followUp_date:values?.next_followUp_date ?  moment(values?.next_followUp_date).format('yyyy-MM-DD')  : '',
      next_followup_time: values?.next_followup_time
        ? moment(values?.next_followup_time).format("hh:mm")
        : "",
      new_batch_start: values?.new_batch_start,
      followup_batch_start_course: values?.followup_batch_start_course?.map(
        (course: any) => course.value
      ),
      next_followup_reason: values?.next_followup_reason,
      course_not_clear: values?.course_not_clear,
      interested_course: values?.interested_course?.map((el: any) => {
        return {
          course_name:
            el?.course_name === ""
              ? null
              : el?.course_name?.value
              ? el?.course_name?.value
              : el?.course_name,
          fees: el?.fees?.toString(),
          Offer_Price: el?.Offer_Price,
          remark: el?.remark,
        };
      }),
      disposition_one: values?.disposition_one,
      disposition_two: values?.disposition_two,
    };

    editFollowUp({
      id: followupId,
      body: formattedValues,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          navigate(`/inquiries/edit/${inquiryId}/followup`);
          resetForm();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form>
          {(isLoading || isFetching) && (
            <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
              <CircularProgress />
            </div>
          )}
          <FollowUpForm formikProps={formikProps} />
        </Form>
      )}
    </Formik>
  );
};

export default EditFollowUpFormWrapper;
